// src/services/api/base.ts
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse, ApiError } from '../../types/api';
import { oktaAuth, restoreSession, redirectToLogin } from '../../lib/okta/oktaConfig';

// Base API configuration
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Preferisci il token Okta corrente (può essere stato rinnovato) a quello in cache
        const token = oktaAuth.tokenManager.getTokensSync().accessToken?.accessToken ?? this.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && original) {
          // Un solo tentativo di ripristino sessione, poi ripeti la richiesta
          if (!original._retry && (await restoreSession())) {
            original._retry = true;
            const token = oktaAuth.tokenManager.getTokensSync().accessToken?.accessToken;
            if (token) {
              this.setToken(token);
            }
            return this.client.request(original);
          }
          this.clearToken();
          redirectToLogin();
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: any): ApiError {
    const timestamp = new Date().toISOString();
    
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data?.message || 'Errore del server',
        code: error.response.status.toString(),
        details: error.response.data,
        timestamp,
      };
    } else if (error.request) {
      // Request made but no response received
      return {
        message: 'Errore di connessione. Verifica la connessione internet.',
        code: 'NETWORK_ERROR',
        timestamp,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'Errore sconosciuto',
        code: 'UNKNOWN_ERROR',
        timestamp,
      };
    }
  }

  public setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  public clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  public getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get(url, config);
    return response.data;
  }

  public async post<T, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  public async put<T, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  public async patch<T, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete(url, config);
    return response.data;
  }

  public async upload<T>(url: string, file: File | FormData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const formData = file instanceof FormData ? file : new FormData();
    if (file instanceof File) {
      formData.append('file', file);
    }

    // Costruisci la URL corretta
    let baseUrl = (this.client.defaults.baseURL || '').replace(/\/+$/, ''); // Remove trailing slashes
    let relativeUrl = url.startsWith('/') ? url : `/${url}`; // Ensure leading slash
    const fullUrl = `${baseUrl}${relativeUrl}`;
    const headers: Record<string, string> = {};

    // Aggiungi il token di autorizzazione se presente
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    // Aggiungi headers aggiuntivi dalla config se forniti
    if (config?.headers) {
      Object.assign(headers, config.headers);
    }

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;
  }

  public async download(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.client.get(url, {
      ...config,
      responseType: 'blob',
    });
    return response.data;
  }

  public buildUrl(endpoint: string, params?: Record<string, any>): string {
    const url = new URL(endpoint, BASE_URL);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => url.searchParams.append(key, v.toString()));
          } else {
            url.searchParams.append(key, value.toString());
          }
        }
      });
    }
    
    return url.toString();
  }

  public getBaseUrl(): string {
    return BASE_URL;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Utility function to build query parameters
export function buildQueryParams(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v.toString()));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });
  
  return searchParams.toString();
}

// Utility function to handle API errors
export function handleApiError(error: any): ApiError {
  if (error.response) {
    return {
      message: error.response.data?.message || 'Errore del server',
      code: error.response.status.toString(),
      details: error.response.data,
      timestamp: new Date().toISOString(),
    };
  } else if (error.request) {
    return {
      message: 'Errore di connessione',
      code: 'NETWORK_ERROR',
      timestamp: new Date().toISOString(),
    };
  } else {
    return {
      message: error.message || 'Errore sconosciuto',
      code: 'UNKNOWN_ERROR',
      timestamp: new Date().toISOString(),
    };
  }
}

// Utility function to create form data
export function createFormData(data: Record<string, any>): FormData {
  const formData = new FormData();
  
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item instanceof File) {
            formData.append(`${key}[${index}]`, item);
          } else {
            formData.append(`${key}[${index}]`, item.toString());
          }
        });
      } else {
        formData.append(key, value.toString());
      }
    }
  });
  
  return formData;
}

// Default export
export default apiClient;
